/**
 * Module 3: read the mail, confirm the customer (2 minutes). The customer line sits in
 * the mail card at the top of the case, under the sender: Confirmed, Confirm <Name>,
 * Not them, Why, Find customer. A suggestion is not a confirmation, confirm only with
 * evidence, prices follow.
 *
 * Names match practice/facts.ts: PRACTICE-0001 Example Foundry Ltd, Leeds (evidence:
 * sender domain); PRACTICE-0002 guess Sample Castings GmbH, Kassel, right customer
 * Sample Castings A/S, Aarhus. Round 3 (12 Oct 2026): Simplified Technical English.
 */

import type { Module } from "@/features/training/types";

import { checkSlide } from "../check-slide";

export const MODULE: Module = {
  id: "customer",
  title: "Read the mail, confirm the customer",
  menuTitle: "Customer",
  minutes: 2,
  kind: "lesson",
  goals: [
    "Read the mail first. It is the card at the top of the case, with the customer line in it.",
    "Know that a suggested customer is not a confirmed one.",
    "Confirm only when `Why` shows evidence.",
  ],
  helpTopics: ["customer"],
  slides: [
    {
      id: "c1",
      tone: "light",
      title: "When you need to confirm who the customer is",
      job: "When you need to confirm who the customer is, read the mail and the customer line.",
      path: ["Case", "Customer line", "Why"],
      body: [
        {
          kind: "text",
          text: "Step one, **Read the mail**. The mail is a compact card at the top of the case, above the draft. It shows who wrote, the attachments as chips, and the customer's own words. Long text is folded to 6 lines with `Read all`. A follow-up or a reminder is one line under the subject. The customer line sits in that card, under the sender, with its icon:",
        },
        {
          kind: "list",
          items: [
            "`Confirmed`: I2Q is sure. Nothing to do here.",
            "`Confirmed by sales`: a sales person confirmed the account. Nothing to do here.",
            "`Confirm Example Foundry Ltd, Leeds`: read the evidence under `Why`, then click. Shortcut {key:c}.",
            "`Not them`: the suggestion is wrong. One click opens the search.",
            "`Find customer`: I2Q found no account for the sender. Search by name or account number.",
          ],
        },
        {
          kind: "text",
          text: "The big button under the draft says the same until the customer is sure: `Confirm Example Foundry Ltd, Leeds` or `Find customer`. When two accounts fit, it says `Pick the customer` and opens the search.",
        },
      ],
      interaction: {
        id: "c1-card",
        kind: "hotspots",
        required: true,
        minOpen: 2,
        stage: { view: "case", caseId: "PRACTICE-0001", part: "customer" },
        spots: [
          {
            anchor: "case-customer",
            label: "Customer line",
            text: "Example Foundry Ltd, Leeds is a suggestion. Open `Why` and read the evidence before you confirm.",
          },
          {
            anchor: "case-action",
            label: "The one action",
            text: "The big button names the next step. Here it is the customer, then the draft, then the reply.",
          },
        ],
      },
      check: "Open `Why` every time. A name alone is not evidence.",
      labels: ["Confirmed", "Confirmed by sales", "Not them", "Why", "Find customer"],
    },
    {
      id: "c2",
      tone: "grey",
      title: "Confirm only when the evidence is there",
      job: "Read the evidence, then confirm the customer.",
      path: ["Case", "Customer line", "Why"],
      body: [
        {
          kind: "text",
          text: "Good evidence is concrete: the sender's domain matched earlier inquiries, the account number is in the mail, or the address matches.",
        },
        {
          kind: "text",
          text: 'When an agent, a trader or a distributor names an end customer, I2Q shows both. The party with "Quoted first" is the account to quote. In India and the UK that is the agent, in the other entities the end customer, unless the earlier quotations of the sender say otherwise.',
        },
      ],
      interaction: {
        id: "c2-confirm",
        kind: "practice",
        required: true,
        stage: { view: "case", caseId: "PRACTICE-0001", part: "customer" },
        task: "confirm-customer-with-evidence",
        steps: [
          "Click `Why` on the customer line.",
          "Read the evidence: Sender domain matched 6 earlier inquiries.",
          "Click `Confirm Example Foundry Ltd, Leeds`.",
        ],
        success:
          "Example Foundry Ltd, Leeds is confirmed. The evidence was clear. The case is yours from the first click.",
        hints: [
          "`Why` is the small link on the customer line, next to the Confirm button.",
          "The evidence names where I2Q found the customer. Here it is the sender's domain.",
        ],
      },
      check: "No evidence, no confirmation. Open the mail and look for the customer yourself.",
      labels: ["Why"],
    },
    {
      id: "c3",
      tone: "light",
      title: "A guess in the wrong country",
      job: "Reject a guess and find the right customer.",
      path: ["Case", "Customer line", "Not them", "Find customer"],
      body: [
        {
          kind: "text",
          text: "This mail came from Denmark. I2Q guesses Sample Castings GmbH in Kassel, Germany. There is no evidence for it.",
        },
      ],
      interaction: {
        id: "c3-pick",
        kind: "practice",
        required: true,
        stage: { view: "case", caseId: "PRACTICE-0002", part: "customer" },
        task: "reject-guess-pick-another",
        steps: [
          "Click `Why`. There is no evidence.",
          "Click `Not them`. Search for Sample Castings.",
          "Pick Sample Castings A/S, Aarhus.",
        ],
        success:
          "Sample Castings A/S, Aarhus is confirmed. The guess was in the wrong country. You stopped a quotation to the wrong customer.",
        hints: [
          "If you confirm the guess, the practice shows why that was wrong: There is no evidence for this account, and it is in another country.",
          "The search box takes a name or an account number.",
        ],
      },
      check: "Same name, different country? Check the sender and the address in the mail.",
      labels: ["Not them", "Find customer", "Why"],
    },
    {
      id: "c4",
      tone: "grey",
      title: "No prices yet? Check the customer first.",
      job: "Compare the draft before and after you confirm the customer.",
      body: [
        {
          kind: "text",
          text: "Prices depend on the customer. I2Q shows them after you confirm the customer. This is by design, not a fault.",
        },
        {
          kind: "note",
          tone: "warning",
          title: "A wrong confirmation teaches I2Q the wrong thing",
          text: "I2Q learns from what you confirm. If you confirm the wrong customer, the next mail from that sender may get the same wrong customer.",
        },
      ],
      interaction: {
        id: "c4-before-after",
        kind: "before-after",
        required: false,
        tabs: [
          {
            id: "c4-before",
            label: "Before you confirm",
            stage: { view: "case", caseId: "PRACTICE-0002", part: "lines" },
          },
          {
            id: "c4-after",
            label: "After you confirm",
            stage: {
              view: "case",
              caseId: "PRACTICE-0002",
              part: "lines",
              after: "customer-confirmed",
            },
          },
        ],
      },
      check: "No row shows a price? Look at the customer line first.",
    },
    {
      id: "c5",
      tone: "light",
      title: "Check: to confirm in the queue",
      job: "Click the right option to finish the module.",
      interaction: {
        id: "c5-check",
        kind: "check",
        required: true,
        question: {
          id: "c5-q",
          topic: "customer",
          stem: "The queue row says `to confirm` under the customer name. The action word is `Confirm`. What do you do?",
          options: [
            {
              id: "c5-q-a",
              text: "Confirm it from the queue. It saves time.",
              correct: false,
              fb: "No. The queue has no confirm button. Read the evidence on the case, then confirm the suggestion.",
            },
            {
              id: "c5-q-b",
              text: "Open the case, read the mail and click `Why` on the customer line.",
              correct: true,
              fb: "Right. Confirm only with evidence. If the guess is wrong, click `Not them` and search.",
            },
            {
              id: "c5-q-c",
              text: "Set the case to Not for us.",
              correct: false,
              fb: "No. The inquiry is still real. Only the customer is unclear.",
            },
            {
              id: "c5-q-d",
              text: "Confirm it and send feedback later.",
              correct: false,
              fb: "No. Feedback does not change the case. A wrong confirmation teaches I2Q the wrong thing.",
            },
          ],
        },
      },
      check: "`to confirm` means: no decision yet. Open the mail.",
      labels: ["to confirm", "Confirm"],
    },
    checkSlide({
      id: "c6",
      tone: "grey",
      title: "Check: evidence",
      topic: "customer",
      stem: "Which of these is evidence for a customer?",
      answers: [
        [
          "The name sounds like a customer you know.",
          false,
          "No. The same name can exist in two countries.",
        ],
        [
          "Sender domain matched 6 earlier inquiries.",
          true,
          "Right. That is concrete evidence from earlier mail.",
        ],
        [
          "I2Q put it first in the list.",
          false,
          "No. First in the list is a suggestion, not evidence.",
        ],
        [
          "The customer is in a big country.",
          false,
          "No. Size says nothing about who wrote the mail.",
        ],
      ],
      check: "Evidence names where I2Q found the customer. Read it under Why.",
      labels: ["Why"],
    }),
    checkSlide({
      id: "c7",
      tone: "light",
      title: "Check: correct it later?",
      topic: "customer",
      stem: "Why not confirm a guess and correct it later?",
      answers: [
        ["It is fine. Feedback corrects it.", false, "No. Feedback does not change the case."],
        [
          "Prices and lines follow the wrong customer, and I2Q learns the wrong thing.",
          true,
          "Right. A wrong confirmation costs more than a careful one.",
        ],
        [
          "Confirming takes too long.",
          false,
          "No. Confirming is one click. The check is what matters.",
        ],
        [
          "Your manager has to approve every change.",
          false,
          "No. You can change it. But the damage is often done by then.",
        ],
      ],
      check: "Confirm once, and confirm the right customer.",
    }),
  ],
};
